import React, { MouseEventHandler } from 'react'
import DatePickerOptions from './datepicker-options'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import 'scss/ds3-elements/datepicker.scss' //required to override custom calendar design
import Button from 'app/modules/shared/components/button'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import { getLocale, getLocaleDateString, getLocaleMonthString } from '../helpers'
import { PickerDate, PickerValue, Range, DatePickerOptionValues } from './types'
import IconButton from '../icon-button'

const DatePickerCalendar = ({
  onClose,
  onDateSelect,
  onOptionSelect,
  onApply,
  dateRange,
  selectedOption,
}: {
  onClose: () => void
  onDateSelect: (index: number) => (value: PickerValue, event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  onOptionSelect: MouseEventHandler<HTMLElement>
  onApply: () => void
  dateRange: Range<PickerDate>
  selectedOption: DatePickerOptionValues
}) => {
  const { _t } = useTypedTranslation()

  const dateRangeLabel = `${getLocaleDateString(dateRange[0])}${
    dateRange[0]?.setHours(0, 0, 0, 0) === dateRange[1]?.setHours(0, 0, 0, 0)
      ? ''
      : ` - ${getLocaleDateString(dateRange[1])}`
  }`

  return (
    <div className="d-block">
      <div className="datepicker-expanded-header">
        <h4>{dateRangeLabel}</h4>
        <IconButton iconClass="ds3-icon ds3-close ds3-hover" onClick={onClose} />
      </div>
      <div className="d-flex flex-column flex-lg-row">
        <div className="datepicker-expanded-options-list">
          <DatePickerOptions selectedOption={selectedOption} onSelect={onOptionSelect} />
        </div>
        <div className="datepicker-expanded-calendar">
          <Calendar
            next2Label={<i className="ds3-icon ds3-chevron-right-double ds3-hover" />}
            nextLabel={<i className="ds3-icon ds3-chevron-right ds3-hover" />}
            prev2Label={<i className="ds3-icon ds3-chevron-left-double ds3-hover" />}
            prevLabel={<i className="ds3-icon ds3-chevron-left ds3-hover" />}
            navigationLabel={({ date }) => getLocaleMonthString(date)}
            tileClassName={({ date }) => getTileClass(date, dateRange)}
            maxDate={new Date()}
            locale={getLocale()}
            onChange={onDateSelect(0)}
            value={dateRange[0]}
          />
        </div>
        <div className="datepicker-expanded-calendar">
          <Calendar
            next2Label={<i className="ds3-icon ds3-chevron-right-double ds3-hover" />}
            nextLabel={<i className="ds3-icon ds3-chevron-right ds3-hover" />}
            prev2Label={<i className="ds3-icon ds3-chevron-left-double ds3-hover" />}
            prevLabel={<i className="ds3-icon ds3-chevron-left ds3-hover" />}
            navigationLabel={({ date }) => getLocaleMonthString(date)}
            tileClassName={({ date }) => getTileClass(date, dateRange)}
            maxDate={new Date()}
            locale={getLocale()}
            onChange={onDateSelect(1)}
            value={dateRange[1]}
          />
        </div>
      </div>
      <div className="datepicker-expanded-button-group">
        <Button id="datepicker-calendar-cancel" variant="tertiary" onClick={onClose}>
          {_t('cancel')}
        </Button>
        <Button id="datepicker-calendar-apply" variant="primary" onClick={onApply}>
          {_t('apply')}
        </Button>
      </div>
    </div>
  )
}

export default DatePickerCalendar

/**
 *
 * @param tileDate    Date represented by the calendar tile
 * @param dateRange   The actual selected date range
 * @returns           Proper tile classname based on tileDate's relation to dateRange
 */
const getTileClass = (tileDate: Date, dateRange: Range<PickerDate>) => {
  if (
    !dateRange[0] ||
    !dateRange[1] ||
    dateRange[0].getTime() > tileDate.getTime() ||
    tileDate.getTime() > dateRange[1].getTime()
  ) {
    return ''
  }

  return 'react-calendar__tile--selectRange'
}
